import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { costAPI } from '../services/api';

const COLORS = { aws:'#ff9900', gcp:'#4285f4', azure:'#0078d4', digitalocean:'#0080ff', vultr:'#007bfc', lightnode:'#00c853' };

export default function Costs() {
  const [costs,    setCosts]    = useState(null);
  const [forecast, setForecast] = useState(null);
  const [compare,  setCompare]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [days,     setDays]     = useState(30);
  const [specs,    setSpecs]    = useState({ cpu:2, memory:4, hours:720 });
  const [liveCosts, setLiveCosts] = useState(null);
  const [liveAge,   setLiveAge]   = useState(0);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      costAPI.list({ days }),
      costAPI.forecast(),
      costAPI.compare(specs),
    ]).then(([c, f, comp]) => {
      setCosts(c.data.data);
      setForecast(f.data.data);
      setCompare(comp.data.data);
    }).catch(console.error).finally(() => setLoading(false));
    const pollLive = () => costAPI.live().then(r => { setLiveCosts(r.data.data); setLiveAge(0); }).catch(()=>{});
    pollLive();
    const liveTimer = setInterval(pollLive, 30000);
    const ageTimer  = setInterval(() => setLiveAge(a => a+1), 1000);
    return () => { clearInterval(liveTimer); clearInterval(ageTimer); };
  }, [days]);

  const handleCompare = () => {
    costAPI.compare(specs).then(r => setCompare(r.data.data)).catch(console.error);
  };

  // Build chart data — one column per date, each provider a different color segment
  const chartData = [];
  if (costs?.daily) {
    const dates = [...new Set(costs.daily.map(d => d.date))].sort();
    dates.forEach(date => {
      const e = { date: date.slice(5) };
      let hasData = false;
      Object.keys(COLORS).forEach(p => {
        const amt = +costs.daily
          .filter(d => d.date === date && d.provider === p)
          .reduce((s, d) => s + d.amount, 0).toFixed(4);
        e[p] = amt;
        if (amt > 0) hasData = true;
      });
      if (hasData) chartData.push(e);
    });
  }

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div><h1>Cost Management</h1><p>Monitor and optimize your cloud spending</p></div>
        <div style={{ display:'flex', gap:8 }}>
          {[7,14,30,90].map(d => (
            <button key={d} className={`btn btn-sm ${days===d?'btn-primary':'btn-secondary'}`} onClick={() => setDays(d)}>{d}d</button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="stat-grid">
        {(costs?.byProvider || []).map((p, i) => (
          <div className="stat-card" key={p.provider} style={{ '--accent-color': COLORS[p.provider] || '#8899b4' }}>
            <div className="stat-icon" style={{ fontSize:20, textTransform:'uppercase', fontFamily:'var(--font-mono)', color: COLORS[p.provider] || '#8899b4' }}>{p.provider}</div>
            <div className="stat-value">${p.total}</div>
            <div className="stat-label">Last {days} days</div>
          </div>
        ))}
        <div className="stat-card" style={{ '--accent-color':'#10b981' }}>
          <div className="stat-icon">💰</div>
          <div className="stat-value">${costs?.grandTotal || 0}</div>
          <div className="stat-label">Total spend ({days} days)</div>
          <div className="stat-change down">↑ Forecast: ${forecast?.forecastNext}/mo</div>
        </div>
      </div>

      {/* Bar chart */}
      <div className="card" style={{ marginBottom:20 }}>
        <h2 style={{ fontSize:16, marginBottom:20 }}>Daily Cost Breakdown by Provider</h2>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e2d45" />
            <XAxis dataKey="date" tick={{ fill:'#8899b4', fontSize:11 }} />
            <YAxis tick={{ fill:'#8899b4', fontSize:11 }} tickFormatter={v=>`$${v}`} />
            <Tooltip contentStyle={{ background:'#111827', border:'1px solid #1e2d45', borderRadius:8, color:'#f0f4ff' }} formatter={(v)=>[`$${v}`,'']} />
            <Legend wrapperStyle={{ fontSize:12 }} />
            {Object.entries(COLORS).filter(([k]) => (costs?.byProvider||[]).some(p => p.provider === k)).map(([k,v]) => <Bar key={k} dataKey={k} stackId="a" fill={v} />)}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Compare + Forecast row */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>

        {/* Cloud Comparison */}
        <div className="card">
          <h2 style={{ fontSize:16, marginBottom:16 }}>Price Comparison Calculator</h2>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:16 }}>
            {['cpu','memory','hours'].map(k => (
              <div className="form-group" key={k} style={{ marginBottom:0 }}>
                <label>{k.toUpperCase()}{k==='cpu'?' vCPUs':k==='memory'?' GB':' hrs'}</label>
                <input className="form-control" type="number" min="1" value={specs[k]}
                  onChange={e => setSpecs({ ...specs, [k]: +e.target.value })} />
              </div>
            ))}
          </div>
          <button className="btn btn-primary btn-sm" onClick={handleCompare}>Calculate →</button>
          {compare && (
            <div style={{ marginTop:16 }}>
              {Object.entries(compare.pricing || {}).sort((a,b)=>a[1].monthly-b[1].monthly).map(([p,v]) => (
                <div key={p} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
                  <span style={{ width:100, fontSize:12, fontFamily:'var(--font-mono)', textTransform:'uppercase', color: COLORS[p], fontWeight:700 }}>{p}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ height:6, background:'var(--bg-hover)', borderRadius:3, overflow:'hidden' }}>
                      <div style={{ width:`${Math.min((v.monthly / Math.max(...Object.values(compare.pricing).map(x=>x.monthly)))*100, 100)}%`, height:'100%', background: COLORS[p], borderRadius:3 }} />
                    </div>
                  </div>
                  <span style={{ width:70, textAlign:'right', fontFamily:'var(--font-mono)', fontSize:13, fontWeight:700 }}>${v.monthly}/mo</span>
                  {v.savings > 0 && <span style={{ fontSize:11, color:'var(--red)', width:60 }}>+${v.savings}</span>}
                  {v.savings === 0 && <span className="badge badge-green" style={{ fontSize:10 }}>Cheapest</span>}
                </div>
              ))}
              <div style={{ marginTop:12, padding:12, background:'var(--green-bg)', borderRadius:8, border:'1px solid rgba(16,185,129,0.2)' }}>
                <span style={{ fontSize:13, color:'var(--green)' }}>✅ Recommended: <strong>{compare.recommended?.toUpperCase()}</strong> — Saves up to ${((Object.values(compare.pricing||{}).reduce((mx,v)=>Math.max(mx,v.monthly),0)) - (Object.values(compare.pricing||{}).reduce((mn,v)=>Math.min(mn,v.monthly),Infinity))).toFixed(2)}/mo</span>
              </div>
            </div>
          )}
        </div>

        {/* Forecast */}
        <div className="card">
          <h2 style={{ fontSize:16, marginBottom:16 }}>Cost Forecast</h2>
          {forecast && (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:20 }}>
                <div style={{ background:'var(--bg-secondary)', borderRadius:10, padding:16 }}>
                  <div style={{ fontSize:11, color:'#4d6080', marginBottom:4 }}>THIS MONTH</div>
                  <div style={{ fontSize:28, fontWeight:700, color:'#f0f4ff' }}>${forecast.currentMonth}</div>
                </div>
                <div style={{ background:'var(--accent-glow)', borderRadius:10, padding:16, border:'1px solid rgba(59,130,246,0.2)' }}>
                  <div style={{ fontSize:11, color:'#4d6080', marginBottom:4 }}>NEXT MONTH</div>
                  <div style={{ fontSize:28, fontWeight:700, color:'var(--accent)' }}>${forecast.forecastNext}</div>
                </div>
              </div>
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:12, color:'#8899b4', marginBottom:10 }}>💡 Optimization Suggestions</div>
                {forecast.suggestions?.map((s, i) => (
                  <div key={i} style={{ display:'flex', gap:8, padding:'8px 0', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                    <span style={{ color:'var(--green)' }}>→</span>
                    <span style={{ color:'#8899b4' }}>{s}</span>
                  </div>
                ))}
              </div>
              <div style={{ padding:12, background:'var(--yellow-bg)', borderRadius:8, border:'1px solid rgba(245,158,11,0.2)', fontSize:13 }}>
                <span style={{ color:'var(--yellow)' }}>⚡ ML Confidence: {forecast.confidence}% | Trend: {forecast.trend}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
